<!-- Container -->
<div class="block relative">

  <!-- Static Label (above chips + input) -->
  @if (hasLabel() && labelMode() === 'static') {
    <label
      [for]="inputId()"
      class="block mb-2 text-sm font-medium text-gray-700"
    >
      {{ label() }}
      @if (hasRequiredRule()) {
        <span class="ml-0.5 text-red-500">*</span>
      }
    </label>
  }

  <!-- Chip list – always above the input field -->
  @if (hasChips()) {
    <div class="chip-list flex flex-wrap gap-1 mb-2">
      @for (chip of chips(); track chip; let i = $index) {
        <p-chip
          class="px-2 py-1"
          [label]="chip"
          [removable]="!computedDisabled() && !readonly()"
          (onRemove)="removeChip(i)"
        />
      }

      @if (!computedDisabled() && !readonly()) {
        <button
          type="button"
          class="chip-clear-all"
          (click)="clearAll()">
          <i class="pi pi-times-circle mr-1"></i>Wyczyść wszystko
        </button>
      }
    </div>
  }

  <!-- Label Mode Switch -->
  @if (labelMode() === 'float' && hasLabel()) {
    <p-floatlabel [variant]="filled() ? 'in' : 'on'" class="block">
      <ng-container *ngTemplateOutlet="inputTemplate"></ng-container>
      <label [for]="inputId()">
        {{ label() }}
        @if (hasRequiredRule()) {
          <span class="ml-0.5 text-red-500">*</span>
        }
      </label>
    </p-floatlabel>
  } @else if (labelMode() === 'ifta' && hasLabel()) {
    <p-iftalabel class="block">
      <ng-container *ngTemplateOutlet="inputTemplate"></ng-container>
      <label [for]="inputId()">
        {{ label() }}
        @if (hasRequiredRule()) {
          <span class="ml-0.5 text-red-500">*</span>
        }
      </label>
    </p-iftalabel>
  } @else {
    <ng-container *ngTemplateOutlet="inputTemplate"></ng-container>
  }

  @if (helpText() && !isInvalid()) {
    <small class="block mt-1 text-xs leading-tight text-gray-500 dark:text-gray-400">
      {{ helpText() }}
    </small>
  }

  @if (showErrors() && isInvalid() && errorMessage()) {
    <small class="block mt-1 text-xs leading-tight text-red-500 dark:text-red-400 animate-fade-in">
      <i class="pi pi-exclamation-circle mr-1"></i>
      {{ errorMessage() }}
    </small>
  }

</div>

<!-- Input Template -->
<ng-template #inputTemplate>
  @if (hasIcon()) {
    <p-iconfield [class]="(fluid() ? 'w-full' : '') + ' block'">
      @if (icon()?.position === 'left') {
        <p-inputicon
          [class]="icon()?.icon"
          [class.cursor-pointer]="icon()?.clickable"
          [class.transition-colors]="icon()?.clickable"
          [class.duration-200]="icon()?.clickable"
          (click)="onIconClick()"
        />
      }
      <ng-container *ngTemplateOutlet="inputElement"></ng-container>
      @if (icon()?.position === 'right') {
        <p-inputicon
          [class]="icon()?.icon"
          [class.cursor-pointer]="icon()?.clickable"
          [class.transition-colors]="icon()?.clickable"
          [class.duration-200]="icon()?.clickable"
          (click)="onIconClick()"
        />
      }
    </p-iconfield>
  } @else {
    <ng-container *ngTemplateOutlet="inputElement"></ng-container>
  }
</ng-template>

<!-- Input Element -->
<ng-template #inputElement>
  @if (useMask()) {
    <p-inputmask
      [id]="inputId()"
      [mask]="mask()!"
      [slotChar]="slotChar()"
      [placeholder]="placeholder()"
      [disabled]="computedDisabled()"
      [readonly]="readonly()"
      [autofocus]="autofocus()"
      [class.w-full]="fluid()"
      [class.opacity-60]="computedDisabled()"
      [class.cursor-not-allowed]="computedDisabled()"
      [class.bg-gray-100]="computedDisabled()"
      [invalid]="isInvalid()"
      [fluid]="fluid()"
      [variant]="filled() ? 'filled' : 'outlined'"
      [attr.name]="name() || null"
      [attr.aria-describedby]="helpText() ? inputId() + '-help' : null"
      [attr.aria-invalid]="isInvalid()"
      (onFocus)="onMaskFocus($event)"
      (onBlur)="onMaskBlur($event)"
      (onInput)="onMaskInput($event)"
      (keydown)="onKeydown($event)"
    />
  } @else {
    <input
      pInputText
      [noWhitespace]="noWhitespace()"
      [uppercase]="uppercase()"
      [id]="inputId()"
      [type]="type()"
      [value]="inputValue()"
      [placeholder]="placeholder()"
      [disabled]="computedDisabled()"
      [readonly]="readonly()"
      [autofocus]="autofocus()"
      [class.w-full]="fluid()"
      [class.opacity-60]="computedDisabled()"
      [class.cursor-not-allowed]="computedDisabled()"
      [class.bg-gray-100]="computedDisabled()"
      [class.border-red-500!]="isInvalid()"
      [variant]="filled() ? 'filled' : 'outlined'"
      [attr.name]="name() || null"
      [attr.maxlength]="maxlength()"
      [attr.aria-describedby]="helpText() ? inputId() + '-help' : null"
      [attr.aria-invalid]="isInvalid()"
      (focus)="onFocus($event)"
      (blur)="onBlur($event)"
      (input)="onInput($event)"
      (keydown)="onKeydown($event)"
    />
  }
</ng-template>
